import React from 'react'
import styled from 'styled-components'
import CrSortingWidget from './ui/sorting-widget-comparative-reports'
import { map } from 'lodash-es'
import ComparativeReportResultRow from './ui/comparative-report-result-row'
import ThemeBadge from './ui/theme-badge'
import {
  getThemeTitleByDriverId,
  getThemeTitleByStatementId,
  getThemeIndexByThemeTitle, getThemeStyle
} from '../utilities/helpers'
import { PHONELG, TABLET, DESKTOP } from '../utilities/breakpoints'
import { connect } from 'react-redux'
import { sortingService } from '../utilities/sortingService'
import ParameterFilter from './ui/parameter-filter'

const mapStateToProps = ({ survey: { showYourResults } }) => {
  return {
    showYourResults,
  };
};


const ComparativeReport = (props) => {
  const {
    useTranslate,
    crSortingType,
    sortAscending,
    onSetCrSortingType,
    onSetSortAscending,
    filters,
    onSetFilters,
    afterGameComparativeParameterStatistics: {
      topic,
      parameters,
      totalResult,
      totalRate
    },
    crSection,
    crReportId,
    catalysts,
    showYourResults,
    respondentParameters
  } = props;

  const resultPercentage = `${totalResult}%`;
  const importancePercentage = totalRate ? `${(totalRate*100)/5}%` : 0;

  // We check if the section is overall or theme and hide the importance
  // We also need to hide sorting options in the sorting widget
  const hideImportance = crSection === "overall" || crSection === "themes";

  // If importance is not calculated and sorting is byRating, we need to change it to byResult
  if (hideImportance && crSortingType === "byRating") {
    onSetCrSortingType("byResult");
    onSetSortAscending(false);
  }

  // Sort the parameters
  let sortedParameters = [...parameters];
  const sortingFunction = sortingService.getSortingFunction(crSortingType);
  sortedParameters.sort(sortingFunction(sortAscending));

  // If there are no parameters we show only "Everyone" bar
  if(sortedParameters.length === 0 && totalResult !== null) {
    sortedParameters = [{
      "label": useTranslate("data_vault.everyone"),
      "result": totalResult,
      "rate": totalRate,
      "parents": [],
    }]
  }

  const getThemeTitle = () => {
    if(crSection === "drivers") {
      return getThemeTitleByDriverId(crReportId, catalysts);
    }
    if(crSection === "statements") {
      return getThemeTitleByStatementId(crReportId, catalysts);
    }
    else {
      return "";
    }
  }

  const handleFiltersChange = value => {
    onSetFilters(value);
  }

  return (
    <ReportContainer>
      {(crSection === "drivers" || crSection === "statements") && getThemeTitle() !== "" &&
        <ThemeBadges className="theme-badges">
          <ThemeBadge
            themeTitle={getThemeTitle()}
            themeIndex={getThemeIndexByThemeTitle(getThemeTitle(), catalysts)}
          />
        </ThemeBadges>
      }
      <ReportTitle>{crSection !== "overall" ? topic : useTranslate("comparative_reports.overall_result_page_title") }</ReportTitle>
      <div className="finish-page__filters">
        {(respondentParameters.length > 0)  && (
          <ParameterFilter
            filters={filters}
            handleFiltersChange={handleFiltersChange}
            respondentParameters={respondentParameters}
            isAfterGameGuest={true}
            useTranslate={useTranslate}
          />
        )}
        <CrSortingWidget useTranslate={useTranslate} sortingType={crSortingType} hideImportance={hideImportance} />
      </div>

      <Results>
        {map(sortedParameters, (parameter, index) => {
          if (!showYourResults && parameter.label === useTranslate("data_vault.you")) {
            return null;
          } else {
            return (
              <ComparativeReportResultRow
                type={crSection}
                barColor={crSection === "themes" ? getThemeStyle(getThemeIndexByThemeTitle(topic, catalysts)).bgColor : "#f58220"}
                label={parameter.label}
                parents={parameter.parents}
                result={parameter.result}
                rate={parameter.rate}
                key={index}
                showLabels={index === 0}
                useTranslate={useTranslate}
                hideImportance={hideImportance}
              />
            );
          }
        })}
        <AverageResultRow className={`result-lines ${hideImportance ? "hide-importance" : 0}`}>
          <div className="col-average" />
          <div className="col-result">
            <div className="col-content">
              <span className="line" style={{left: resultPercentage}} />
            </div>
          </div>
          <div className="col-importance">
            <div className="col-content">
              <span className="line" style={{left: importancePercentage}} />
            </div>
          </div>
        </AverageResultRow>
        <AverageResultRow className={`${hideImportance ? "hide-importance" : 0}`}>
          <div className="col-average">{useTranslate("comparative_reports.average")}</div>
          <div className="col-result">
            <div className="col-content">
              <span className="result" style={{marginLeft: resultPercentage}}>
                {totalResult && totalResult.toFixed(0)}%
              </span>
            </div>
          </div>
          <div className="col-importance">
            <div className="col-content">
              <span className="result" style={{marginLeft: importancePercentage}}>
                {totalRate && totalRate.toFixed(1)}
              </span>
            </div>
          </div>
        </AverageResultRow>
      </Results>
    </ReportContainer>
  )
}

export default connect(mapStateToProps)(ComparativeReport);

const ReportContainer = styled.div`
  background-color: #fff;
  border-radius: 10px;
  padding: 20px 10px;
  
  .finish-page__filters {
    margin-bottom: 3rem;
  }
  
  @media${TABLET} {
    padding: 30px;
  }
  
  @media${DESKTOP} {
    padding: 30px 40px;
  }
`;

const ThemeBadges = styled.div`
  margin: .25rem 0 .75rem;
`;

const ReportTitle = styled.h3`
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 2rem;
`;

const Results = styled.div`
  position: relative;
`;

const AverageResultRow = styled.div`
  width: 100%;
  border-top: 1px solid #5F7D95;
  font-weight: 500;
  padding-top: 10px;
  margin-top: 1rem;
  display: grid;
  grid-template-columns: 1fr repeat(2,21%);
  
  &.result-lines {
    display: none;
    position: absolute;
    border-top: none;
    margin-top: 0;
    left: 0;
    top: 10px;
    width: 100%;
    height: calc(100% - 46px);
    
    .col-result .col-content, 
    .col-importance .col-content {
      height: 100%;
      position: relative;
      .line {
        position: absolute;
        top: 0;
        left: 50%;
        width: 1px;
        height: 100%;
        border-left: 1px dashed #5F7D95;
      }
    }
  }
  
  &.hide-importance {
    .col-importance {
      display: none;
    }
  }
  
  .col-result .col-content{
    max-width: 80px;
  }
  
  .col-importance .col-content{
    max-width: 90px;
  }
  
  @media${PHONELG} {
    grid-template-columns: 1fr repeat(2, 150px);
    grid-column-gap: 20px;
    
    &.result-lines {
      display: grid;
    }
  }
  
  @media (min-width: 1000px) {
    grid-template-columns: 1fr 50% 150px;
    grid-column-gap: 30px;
    
    .col-result .col-content{
      max-width: 100%;
    } 
  }
  
  @media (max-width: 599px) {
    .col-result .col-content{
      .result {
        margin-left: 0 !important;
      }
    }
    
    .col-importance .col-content{
      .result {
        margin-left: 0 !important;
      }
    }
  }
`;


// WEBPACK FOOTER //
// ./src/components/comparative-report.js